const IMPERSONATION_BACKUP_KEY = "impersonationBackup";
export const IMPERSONATION_FLOW_APPLICANT = "applicant";
export const IMPERSONATION_CHANGED_EVENT = "bzu-impersonation-changed";

export function notifyImpersonationChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(IMPERSONATION_CHANGED_EVENT));
  }
}

export function decodeAccessPayload(accessToken) {
  if (!accessToken) return null;
  try {
    const payload = accessToken.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(normalized);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export function isImpersonationActive(accessToken) {
  const payload = decodeAccessPayload(accessToken);
  return !!payload?.impersonation?.active;
}

export function isApplicantImpersonationFlow(accessToken) {
  const payload = decodeAccessPayload(accessToken);
  return payload?.impersonation?.flow === IMPERSONATION_FLOW_APPLICANT;
}

export function canAccessApplicantImpersonationUI(accessToken) {
  if (!accessToken) return false;
  return !isImpersonationActive(accessToken);
}

export function shouldHideStudentLogout() {
  const token = sessionStorage.getItem("token");
  return isApplicantImpersonationFlow(token);
}

function normalizeStoredUser(user) {
  if (!user) return null;
  const role = user.Role || user.role || {};
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role: role.name || user.role,
    role_id: role.id || user.role_id,
  };
}

export function applyAuthSessionToStorage(data) {
  if (!data) return;
  const accessToken = data.accessToken || data.token;
  if (accessToken) {
    sessionStorage.setItem("token", accessToken);
  }
  if (data.refreshToken) {
    sessionStorage.setItem("refreshToken", data.refreshToken);
  }
  if (data.user) {
    sessionStorage.setItem("user", JSON.stringify(normalizeStoredUser(data.user)));
  }
  if (data.rolePermissions != null) {
    sessionStorage.setItem("rolePermissions", JSON.stringify(data.rolePermissions));
  }
  if (data.department_id != null) {
    sessionStorage.setItem("department_id", JSON.stringify(data.department_id));
  }
}

export function buildImpersonationBackup() {
  return {
    token: sessionStorage.getItem("token"),
    refreshToken: sessionStorage.getItem("refreshToken"),
    user: sessionStorage.getItem("user"),
    rolePermissions: sessionStorage.getItem("rolePermissions"),
    department_id: sessionStorage.getItem("department_id"),
  };
}

export function setImpersonationBackup(backup) {
  sessionStorage.setItem(IMPERSONATION_BACKUP_KEY, JSON.stringify(backup));
}

export function getImpersonationBackup() {
  const raw = sessionStorage.getItem(IMPERSONATION_BACKUP_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function removeImpersonationBackup() {
  sessionStorage.removeItem(IMPERSONATION_BACKUP_KEY);
}

export function restoreImpersonationBackup(backup) {
  if (!backup) return;
  if (backup.token) sessionStorage.setItem("token", backup.token);
  else sessionStorage.removeItem("token");
  if (backup.refreshToken) sessionStorage.setItem("refreshToken", backup.refreshToken);
  else sessionStorage.removeItem("refreshToken");
  if (backup.user) sessionStorage.setItem("user", backup.user);
  else sessionStorage.removeItem("user");
  if (backup.rolePermissions) {
    sessionStorage.setItem("rolePermissions", backup.rolePermissions);
  } else {
    sessionStorage.removeItem("rolePermissions");
  }
  if (backup.department_id) {
    sessionStorage.setItem("department_id", backup.department_id);
  } else {
    sessionStorage.removeItem("department_id");
  }
}

export async function impersonateApplicant(targetUserId) {
  const token = sessionStorage.getItem("token");
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/auth/impersonate-applicant`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ target_user_id: targetUserId }),
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Failed to impersonate applicant");
  }
  return data;
}

export async function stopApplicantImpersonate() {
  const token = sessionStorage.getItem("token");
  const response = await fetch(
    `${process.env.REACT_APP_BASE_URL}/auth/stop-applicant-impersonate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    }
  );
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || data.error || "Failed to exit impersonation");
  }
  return data;
}
